/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import { IconButton, Menu, Box, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  StyledHeaderCellTypo,
  StyledBodyCellTypo,
  StyledMenuItem,
  StyledTypography,
} from "../Users.jsx";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useState } from "react";
import PropTypes from "prop-types";
import KeyIcon from "@mui/icons-material/Key";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import DeleteIcon from "@mui/icons-material/Delete";
import MFAModal from "../modals/MFAModal.jsx";
import DeleteUserModal from "../modals/DeleteUserModal.jsx";
import UserDetailsModal from "../modals/UserDetailsModal.jsx";
import { useDispatch } from "react-redux";
import {
  reactivateUser,
  toggleMFAAuth,
} from "../../../../Services/redux/global-admin-users/GlobalAdminUsersActions.js";

const DeactivatedTableData = ({
  searchTerm,
  deactivatedData,
  currentTabStatus,
}) => {
  const readOnly_GlobalAdmin =
    sessionStorage.getItem("readonly_GlobalAdmin") === "true";
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [openMFAModal, setOpenMFAModal] = useState(false);
  const [openDeleteUserModal, setOpenDeleteUserModal] = useState(false);
  const [openUserDetailsModal, setOpenUserDetailsModal] = useState(false);

  const [userDetails, setUserDetails] = useState(null);
  const [mfaEnabled, setMfaEnabled] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Sorting state
  const [sortBy, setSortBy] = useState("companyName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [hoveredColumn, setHoveredColumn] = useState(null);

  // Common icon styles
  const sortIconStyle = {
    color: "#12141599",
    height: "16px",
    width: "16px",
  };

  // Reusable renderSortableHeader function
  const renderSortableHeader = (fieldName, displayName) => (
    <Box
      sx={{ textAlign: "center", display: "flex", alignItems: "center" }}
      onMouseEnter={() => setHoveredColumn(fieldName)}
      onMouseLeave={() => setHoveredColumn(null)}
    >
      <StyledHeaderCellTypo>{displayName}</StyledHeaderCellTypo>
      <IconButton
        sx={{
          cursor: "pointer",
          borderRadius: "4px",
          marginLeft: "8px",
          width: "24px",
          height: "24px",
          padding: "4px",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
        onClick={() => handleSort(fieldName)}
      >
        {sortBy === fieldName && sortOrder === "asc" && (
          <ArrowUpwardIcon sx={{ ...sortIconStyle, transition: "color 0.2s ease" }} />
        )}
        {sortBy === fieldName && sortOrder !== "asc" && (
          <ArrowDownwardIcon sx={{ ...sortIconStyle, transition: "color 0.2s ease" }} />
        )}
        {sortBy !== fieldName && hoveredColumn === fieldName && (
          <ArrowUpwardIcon sx={{ ...sortIconStyle, opacity: 0.5, transition: "opacity 0.2s ease" }} />
        )}
      </IconButton>
    </Box>
  );

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const normalizeValue = (value) => {
    if (value === null || value === undefined) return "";
    return typeof value === "string" ? value.toLowerCase() : value;
  };

  const sortedData = [...(deactivatedData || [])].sort((a, b) => {
    const aValue = normalizeValue(a[sortBy]);
    const bValue = normalizeValue(b[sortBy]);
    let comparison = 0;
    if (aValue > bValue) {
      comparison = 1;
    } else if (aValue < bValue) {
      comparison = -1;
    }
    return sortOrder === "asc" ? comparison : -comparison;
  });

  const handleReactivateUser = () => {
    const userData = deactivatedData.find((user) => user.id === selectedRowId);
    dispatch(reactivateUser(userData.id, searchTerm.trim()));
    setAnchorEl(null);
  };

  const handleUserDetailsOpen = (id) => {
    const details = deactivatedData.find((user) => user.id === id);
    setUserDetails(details);
    setOpenUserDetailsModal(true);
  };

  const handleUserDetailsClose = () => {
    setOpenUserDetailsModal(false);
  };

  const handleVertIconClick = (event, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(index);
    const userData = deactivatedData.find((user) => user.id === index);
    setMfaEnabled(userData?.customMFAEnabled);
  };

  const handleVertIconModalClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleMFAOpen = () => {
    const userData = deactivatedData.find((user) => user.id === selectedRowId);
    setUserDetails(userData);
    setOpenMFAModal(true);
  };

  const handleMFAClose = () => {
    try {
      const data = deactivatedData.find((user) => user.id === selectedRowId);
      const payload = {
        status: ["DEACTIVATED"],
      };
      // Only add email to payload if searchTerm is not empty
      if (searchTerm && searchTerm.trim() !== "") {
        payload.email = [searchTerm.trim()];
      }
      dispatch(
        toggleMFAAuth({ ...data, customMFAEnabled: !mfaEnabled }, payload)
      );
    } catch (error) {
      console.error("MFA toggle error:", error);
    } finally {
      setOpenMFAModal(false);
      setAnchorEl(null);
    }
  };

  const handleMFACancel = () => {
    setOpenMFAModal(false);
    setAnchorEl(null);
  };

  const handleDeleteUserClose = () => {
    setOpenDeleteUserModal(false);
  };

  const handleDeleteUserOpen = () => {
    setOpenDeleteUserModal(true);
  };

  // DataGrid column definitions
  const columns = [
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderHeader: () => renderSortableHeader("email", "Email"),
      renderCell: (params) => (
        <Box
          onClick={() => handleUserDetailsOpen(params.row.id)}
          sx={{
            cursor: "pointer",
            padding: "8px",
            width: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          {(() => {
            const email = params.row.email || "...";
            const displayEmail =
              typeof email === "string" && email.length > 25
                ? email.substring(0, 25) + "..."
                : email;
            const showTooltip = email !== "...";
            return (
              <Tooltip
                title={showTooltip ? email : ""}
                arrow
                placement="top"
                disableHoverListener={!showTooltip}
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "#121415F2",
                      padding: "8px 12px 8px 12px",
                      "& .MuiTooltip-arrow": {
                        color: "#121415F2",
                      },
                    },
                  },
                }}
              >
                <StyledBodyCellTypo
                  sx={{
                    lineHeight: "24px",
                    letterSpacing: "0.1px",
                  }}
                >
                  {displayEmail}
                </StyledBodyCellTypo>
              </Tooltip>
            );
          })()}
        </Box>
      ),
    },
    {
      field: "companyName",
      headerName: "Publisher",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderHeader: () => renderSortableHeader("companyName", "Publisher"),
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px",
          }}
        >
          {(() => {
            const publisher = params.row.companyName || "...";
            const showTooltip = publisher !== "...";
            return (
              <Tooltip
                title={showTooltip ? publisher : ""}
                arrow
                placement="top"
                disableHoverListener={!showTooltip}
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "#121415F2",
                      padding: "8px 12px 8px 12px",
                      "& .MuiTooltip-arrow": {
                        color: "#121415F2",
                      },
                    },
                  },
                }}
              >
                <StyledBodyCellTypo>{publisher}</StyledBodyCellTypo>
              </Tooltip>
            );
          })()}
          <IconButton
            onClick={(e) => handleVertIconClick(e, params.row.id)}
            disabled={readOnly_GlobalAdmin}
          >
            <MoreVertIcon
              sx={{
                height: "16px",
                width: "16px",
                color: "#12141599",
              }}
            />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Common DataGrid style constants
  const GRID_COLORS = {
    headerBg: "#E8E8E8",
    headerText: "#121415",
    border: "rgba(18, 20, 21, 0.2)",
    hoverBg: "rgba(0, 0, 0, 0.04)",
  };

  const commonTextStyles = {
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <DataGrid
          rows={sortedData || []}
          columns={columns}
          getRowId={(row) => row.id}
          pagination
          pageSizeOptions={[5, 10, 15]}
          paginationMode="client"
          disableColumnMenu
          disableRowSelectionOnClick
          loading={false}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "Items per page:",
            },
          }}
          sx={{
            "& .MuiDataGrid-columnHeader": {
              ...commonTextStyles,
              fontWeight: "600",
              backgroundColor: `${GRID_COLORS.headerBg} !important`,
              color: GRID_COLORS.headerText,
              lineHeight: "30px",
              padding: "0px 10px",
              borderBottom: `1px solid ${GRID_COLORS.border}`,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              ...commonTextStyles,
              fontWeight: "bold",
              justifyContent: "flex-start",
              minHeight: "30px",
              lineHeight: "30px",
              padding: "16px",
            },
            "& .MuiDataGrid-cell": {
              ...commonTextStyles,
              borderRight: `1px solid ${GRID_COLORS.border}`,
              borderBottom: "none !important",
              lineHeight: "20px",
              padding: "0px",
            },
            "& .MuiDataGrid-cell:last-child": { borderRight: "none" },
            "& .MuiDataGrid-container--top [role=row]": {
              background: `${GRID_COLORS.headerBg} !important`,
            },
            "& .MuiDataGrid-virtualScroller": {
              marginTop: "0px !important",
            },
            "& .MuiDataGrid-virtualScrollerContent": {
              overflowX: "hidden !important",
            },
            "& .MuiDataGrid-row": {
              height: "56px",
              borderBottom: `1px solid ${GRID_COLORS.border}`,
              "&:hover": {
                backgroundColor: GRID_COLORS.hoverBg,
              },
            },
          }}
        />

        <Menu
          sx={{
            minWidth: "164px",
            minHeight: "118px",
            borderRadius: "4px",
            padding: "4px",
            display: "flex",
            gap: "2px",
          }}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleVertIconModalClose}
        >
          <StyledMenuItem
            sx={{
              color: "#2A2C2D",
            }}
            onClick={handleMFAOpen}
          >
            <KeyIcon
              sx={{
                color: "#12141599",
                height: "16px",
                width: "16px",
              }}
            />
            <StyledTypography>
              {mfaEnabled ? "Disable MFA" : "Enable MFA"}
            </StyledTypography>
          </StyledMenuItem>

          <StyledMenuItem
            sx={{
              color: "#2A2C2D",
            }}
            onClick={handleReactivateUser}
          >
            <PersonOffIcon
              sx={{
                height: "16px",
                width: "16px",
              }}
            />
            <StyledTypography>Reactivate User</StyledTypography>
          </StyledMenuItem>

          <hr />

          <StyledMenuItem
            sx={{
              color: "#B50B00",
            }}
            onClick={handleDeleteUserOpen}
          >
            <DeleteIcon
              sx={{
                height: "16px",
                width: "16px",
              }}
            />
            <StyledTypography>Delete</StyledTypography>
          </StyledMenuItem>
        </Menu>
      </Box>

      <MFAModal
        openMFAModal={openMFAModal}
        handleMFAClose={handleMFAClose}
        handleMFACancel={handleMFACancel}
        status={mfaEnabled}
      />

      <DeleteUserModal
        openDeleteUserModal={openDeleteUserModal}
        handleDeleteUserClose={handleDeleteUserClose}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        currentTabStatus={currentTabStatus}
        selectedRowId={selectedRowId}
        searchTerm={searchTerm}
      />

      <UserDetailsModal
        openUserDetailsModal={openUserDetailsModal}
        handleUserDetailsClose={handleUserDetailsClose}
        userDetails={userDetails}
        currentTabStatus={currentTabStatus}
      />
    </>
  );
};

DeactivatedTableData.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  deactivatedData: PropTypes.array.isRequired,
  currentTabStatus: PropTypes.string.isRequired,
};

export default DeactivatedTableData;
